import Selector from "./Components/Selector";
import useAuth from "../Users/hooks/useAuth";
import { useEffect, useState, useCallback } from "react";
import PresentationTitle from "./Components/PresentationTitle";
import DropdownLang from "./Components/DropdownLang";

const YourPresentations = ({ successHandler }) => {
    const { auth, setAuth } = useAuth();
    const [lang, setLang] = useState("Define the language");
    const [presentations, setPresentations] = useState([]);
    const [newTag, setTag] = useState("");
    const [newAuthor, setAuthor] = useState("");
    const [changeList, setChangeList] = useState([]);
    const [changedTags, setChangedTags] = useState([]);
    const [currentPresentation, setPresentation] = useState();
    const [newBookcaseError, setNewBookcaseError] = useState("");
    const [isCommitting, setIsCommitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const sendData = async (outdata, route) => {
        const token = localStorage.getItem("token");
        const datatosend = JSON.stringify(outdata);
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/${route}`;
        
        try {
            const response = await fetch(url, {
                method: "POST",
                body: datatosend,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
                return null;
            }
            
            if (response.ok) {
                const json = await response.json();
                return json;
            }
            
            throw new Error(`Request failed with status: ${response.status}`);
        } catch (error) {
            console.error("API request failed:", error);
            throw error;
        }
    }

    const createPresentation = async (outdata) => {
        return await sendData(outdata, "create-presentation");
    }

    const deletePresentation = async (outdata) => {
        return await sendData(outdata, "delete-presentation");
    }

    const fetchData = async (route, method) => {
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/presentation/${route}`;
        
        try {
            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
                return null;
            }
            
            if (response.ok) {
                const json = await response.json();
                return json;
            }
            
            throw new Error(`Fetch failed with status: ${response.status}`);
        } catch (error) {
            console.error("Fetch data failed:", error);
            throw error;
        }
    }

    const getPresentation = useCallback(async () => {
        setIsLoading(true);
        try {
            const json = await fetchData("get-presentations", "GET");
            if (json) {
                setPresentations(json);
            }
        } catch (error) {
            console.error("Failed to fetch presentations:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const commit = async () => {
        if (isCommitting || changeList.length === 0) return;
        
        setIsCommitting(true);
        try {
            const requests = changeList.map(change => {
                const splits = change.action.split(" ");
                switch (splits[0]) {
                    case "DELETE":
                        return deletePresentation({ title: change.title, language: change.language });
                    case "CREATE":
                        return createPresentation({ title: change.title, language: change.language });
                    default:
                        return Promise.resolve();
                }
            });

            await Promise.all(requests);
            setChangeList([]);
            setChangedTags([]);
            await getPresentation();
        } catch (error) {
            console.error("Commit failed:", error);
            setNewBookcaseError("Failed to commit changes. Please try again.");
        } finally {
            setIsCommitting(false);
        }
    }

    const removeChange = (index) => {
        setChangeList(prev => prev.filter((_, i) => i !== index));
    }

    const addChange = (change) => {
        const changeId = `${change.action}-${change.title}-${change.language}`;
        const isDuplicate = changeList.some(item => 
            `${item.action}-${item.title}-${item.language}` === changeId
        );
        
        if (!isDuplicate) {
            setChangeList(prev => [...prev, { ...change, id: changeId }]);
        }
    }

    const addCreateBookcase = (event) => {
        event.preventDefault();
        setNewBookcaseError("");

        // Check if already in pending changes
        const isAlreadyInChanges = changeList.some(change => 
            change.title === newTag && change.language === lang && change.action === "CREATE"
        );

        if (isAlreadyInChanges) {
            setNewBookcaseError("This presentation is already queued for creation.");
            return;
        }

        // Check if already exists in presentations
        const alreadyExists = presentations.some(p => 
            p.title === newTag && p.language === lang
        );

        if (alreadyExists) {
            setNewBookcaseError("A presentation with this name and language already exists.");
            return;
        }

        if (newTag.trim() === "") {
            setNewBookcaseError("Name for presentation is required.");
            return;
        }

        if (lang === 'Define the language') {
            setNewBookcaseError("The language of the presentation is required.");
            return;
        }

        addChange({
            title: newTag.trim(),
            action: "CREATE",
            language: lang,
        });

        // Reset form
        setTag("");
        setLang("Define the language");
    }

    const addDeleteBookcase = ({ title, language }) => {
        // Check if already in pending changes
        const isAlreadyInChanges = changeList.some(change => 
            change.title === title && change.language === language && change.action === "DELETE"
        );

        if (!isAlreadyInChanges) {
            addChange({
                title: title,
                action: "DELETE",
                language: language
            });
        }
    }

    useEffect(() => {
        getPresentation();
    }, [getPresentation]);

    return (
        <div className="flex flex-col p-6 max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Your Presentations</h1>
                <button 
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                    onClick={() => successHandler(false)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {currentPresentation ? (
                <PresentationTitle 
                    presentation_title={currentPresentation} 
                    successHandler={(state) => setPresentation(state)} 
                />
            ) : (
                <div className="flex flex-col space-y-8">
                    {/* Create Presentation Form */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h2 className="text-lg font-semibold text-blue-600 mb-4 text-center">
                            Add your presentation
                        </h2>
                        <form className="flex flex-col space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Title
                                    </label>
                                    <input 
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        type="text" 
                                        placeholder="Enter presentation title" 
                                        value={newTag} 
                                        onChange={e => setTag(e.target.value)} 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Language
                                    </label>
                                    <DropdownLang 
                                        selectHandler={(event) => setLang(event.target.value)}
                                        elValue={lang}
                                    />
                                </div>
                            </div>

                            {newBookcaseError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-600 text-sm">{newBookcaseError}</p>
                                </div>
                            )}

                            <button 
                                className="w-full md:w-auto px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={addCreateBookcase}
                                disabled={isCommitting}
                            >
                                Add to Queue
                            </button>
                        </form>
                    </div>

                    {/* Pending Changes */}
                    {changeList.length > 0 && (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pending Changes</h3>
                            <div className="space-y-3 mb-4">
                                {changeList.map((change, index) => (
                                    <div key={change.id || index} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center p-3 bg-white rounded-lg border">
                                        <div className="text-gray-700 font-medium text-center">{change.title}</div>
                                        <div className="text-gray-600 text-center">{change.language}</div>
                                        <div className={`text-center font-semibold ${
                                            change.action === "CREATE" ? "text-green-500" : "text-red-500"
                                        }`}>
                                            {change.action}
                                        </div>
                                        <button 
                                            className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
                                            onClick={() => removeChange(index)}
                                            disabled={isCommitting}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button 
                                className="w-full px-6 py-3 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={commit}
                                disabled={isCommitting}
                            >
                                {isCommitting ? "Committing..." : "Commit All Changes"}
                            </button>
                        </div>
                    )}

                    {/* Existing Presentations */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Your Presentations</h3>
                        
                        {isLoading ? (
                            <div className="text-center py-8">
                                <p className="text-gray-600">Loading presentations...</p>
                            </div>
                        ) : presentations.length === 0 ? (
                            <div className="text-center py-8">
                                <p className="text-gray-600">No presentations found. Create your first one above!</p>
                            </div>
                        ) : (
                            <div className="grid gap-3">
                                {presentations.map((presentation, index) => (
                                    <div 
                                        key={`${presentation.title}-${presentation.language}-${index}`}
                                        className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="text-gray-800 font-medium truncate">{presentation.title}</div>
                                        <div className="text-gray-600 text-sm">{presentation.language}</div>
                                        <button 
                                            className="px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors"
                                            onClick={() => setPresentation({ title: presentation.title, language: presentation.language })}
                                        >
                                            View 
                                        </button>
                                        <button 
                                            className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition-colors"
                                            onClick={() => addDeleteBookcase({ 
                                                title: presentation.title, 
                                                language: presentation.language 
                                            })}
                                            disabled={isCommitting}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default YourPresentations;